'use client';

import { ClubCurrencySimpleIcon } from '@wwe-portal/ui/components/icons/club-currency-simple-icon';

import {
  createPortableDialog,
  removeDialog,
  showDialog,
  showDialogById,
  usePortableDialog,
} from '@wwe-portal/ui/components/dialog';

import { PlayerRosterForClubStore } from '@wwe-portal/entities/roster.type';

import { useSubscriptionQuery } from '@wwe-portal/web/providers/queries/subscription';
import { useSubscriptionHistory } from '@wwe-portal/web/utils/club-store/helper';
import { useMutateRecruit } from '@wwe-portal/web/providers/queries/club-store.mutation';
import { NotEnoughClubCoinDialog } from './NotEnoughClubCoinDialog';
import { useGetClubCoins } from '@wwe-portal/web/providers/queries/currency';
import { FC } from 'react';
import { useTranslations } from 'next-intl';
import { UserSubscriptionMutationState } from '../../subscription/type';
import { RosterRecruitmentDialog } from '@wwe-portal/ui/features/roster';

interface RecruitmentDialogProps {
  roster?: PlayerRosterForClubStore;
  recruitPrice?: number;
  hideRosterFilter: () => void;
  subsribeMutation: UserSubscriptionMutationState;
  onClose: () => void;
}

const ButtonContent: FC<{
  isSubscribed: boolean;
  canRecruit: boolean;
  recruitPrice: number;
}> = ({ isSubscribed, canRecruit, recruitPrice }) => {
  const t = useTranslations();
  if (canRecruit)
    return (
      <>
        <ClubCurrencySimpleIcon size="large" className="text-white" />
        <span>{recruitPrice}</span>
      </>
    );

  if (isSubscribed)
    return <>{t('subscription_select_higher_tier_to_recruit')}</>;
  return <>{t('subscription_subscribe_to_recruit')}</>;
};

export const RecruitmentDialog = createPortableDialog(
  ({
    roster,
    recruitPrice,
    hideRosterFilter,
    subsribeMutation,
    onClose,
  }: RecruitmentDialogProps) => {
    const modal = usePortableDialog();

    const { amount: clubCoin } = useGetClubCoins();
    const { currentPlan, abilities } = useSubscriptionQuery();
    const { goAddClubCoin, goToSubscription } = useSubscriptionHistory();

    const closeModal = () => {
      if (isRecruiting || subsribeMutation.isLoading) return;
      onClose();
      modal.remove();
    };

    const { mutate, isPending: isRecruiting } = useMutateRecruit({
      onSuccess: () => {
        onClose();
        modal.remove();
      },
    });

    const handleClickRecruit = (currentRoster: PlayerRosterForClubStore) => {
      if (currentRoster == null || clubCoin == null || recruitPrice == null)
        return;

      if (!abilities.clubStoreAccess) {
        onClose();
        modal.remove();
        hideRosterFilter?.();
        goToSubscription();
        return;
      }

      if (recruitPrice > clubCoin) {
        showDialog(NotEnoughClubCoinDialog);
        return;
      }

      mutate({
        rosterId: currentRoster.rosterId,
      });
    };

    if (roster == null || recruitPrice == null) return null;

    return (
      <RosterRecruitmentDialog
        open={modal.visible}
        onClose={closeModal}
        contentProps={{
          roster,
          fuseUpPrice: recruitPrice,
          clubCoinProps: {
            clubCoin: clubCoin ?? 0,
            onClickAdd: () => {
              goAddClubCoin();
              modal.remove();
              hideRosterFilter?.();
            },
          },
          buttonProps: {
            loading: isRecruiting || subsribeMutation.isLoading,
            disabled:
              abilities.clubStoreAccess &&
              (isRecruiting || subsribeMutation.isLoading),
            onClick: () => handleClickRecruit(roster),
            children: (
              <div className="flex items-center gap-1 text-base">
                <ButtonContent
                  canRecruit={abilities.clubStoreAccess}
                  isSubscribed={currentPlan != null}
                  recruitPrice={recruitPrice}
                />
              </div>
            ),
          },
        }}
      />
    );
  }
);

const ID = 'RecruitmentDialog';

export const useRecruitmentDialog = ({
  roster,
  recruitPrice,
  hideRosterFilter,
  subsribeMutation,
  onClose,
}: RecruitmentDialogProps) => {
  return {
    show: () => showDialogById(ID),
    remove: () => {
      removeDialog(ID);
      onClose();
    },
    dialog: (
      <RecruitmentDialog
        id={ID}
        roster={roster}
        recruitPrice={recruitPrice}
        hideRosterFilter={hideRosterFilter}
        subsribeMutation={subsribeMutation}
        onClose={onClose}
      />
    ),
  };
};
